import React, { forwardRef, useCallback, useContext, useImperativeHandle, useState } from 'react';
import * as PropTypes from 'prop-types';

import Icon from '@components/icon';
import Link from '@components/link';
import Textarea from '@components/textarea';

import { useLocalized } from 'utils/useLocalized';
import { classes } from 'as-ui-kit/utils/styles';
import { FeedbackContext } from 'contexts';

import i18nStrings from './strings';
import styles from './styles.modules.scss';

const FeedbackForm = forwardRef(({ onCancel }, ref) => {
    const strings = useLocalized(i18nStrings);
    const { initFeedbackText, setInitFeedbackText, feedbackWasSent, setFeedbackWasSent, searchId, position, firm } = useContext(FeedbackContext);
    const [feedbackText, setFeedbackText] = useState( initFeedbackText || '');
    const [areaAlert, setAreaAlert] = useState(null);

    const handleSendFeedback = useCallback(() => {
        if (!feedbackText) {
            setAreaAlert(strings.requiredField);
            return;
        }

        const message = {
            searchId,
            position,
            firm: {
                id: firm.id,
                searchCount: firm.searchCount,
                trucksCount: firm.trucksCount,
                actualTrucksCount: firm.actualTrucksCount,
                firmTopType: firm.firmTopType,
            }
        };

        if (typeof window.catchNotification === 'function') {
            window.catchNotification(`[ТОП-3] ${feedbackText}`, 'active_truckers_feedback', message);
        }

        setFeedbackWasSent(true);
    }, [feedbackText, searchId, position, firm, setFeedbackWasSent, strings]);

    const handleOnBlur = useCallback(() => {
        setInitFeedbackText(feedbackText);
    }, [feedbackText, setInitFeedbackText]);

    const handleClosePopover = handleOnBlur;

    useImperativeHandle(ref, () => ({ handleClosePopover }), [handleClosePopover]);

    const handleCancelClick = useCallback((e) => {
        handleOnBlur();
        onCancel(e);
    }, [onCancel, handleOnBlur]);

    const stopPropagation = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    return !feedbackWasSent ? (
        <div className={styles['form']}>
            <Textarea
                maxLength={200}
                placeholder={strings.placeholder}
                value={feedbackText}
                warning={!feedbackText && areaAlert}
                onBlur={handleOnBlur}
                onChange={(event) => setFeedbackText(event.target.value)}
            />
            <div className={styles['actions']}>
                <button
                    className={classes('glz-button', 'glz-is-small', 'glz-is-primary')}
                    onClick={handleSendFeedback}
                    onMouseDown={stopPropagation}
                >
                    {strings.send}
                </button>
                <Link
                    as="button"
                    onClick={handleCancelClick}
                    onMouseDown={stopPropagation}
                >
                    {strings.cancel}
                </Link>
            </div>
        </div>
    ) : (
        <div className={styles['sent']}>
            <Icon
                name="ic_success"
                size="m"
            />
            <span>
                {strings.thanks}
            </span>
        </div>
    );
});


FeedbackForm.displayName = 'FeedbackForm';

export default FeedbackForm;

FeedbackForm.propTypes = {
    onCancel: PropTypes.func,
};
